import styled from 'styled-components';
import { EventType, logAnalyticsEvent } from '@pass-frontend/common/analytics';
import {
  ClientOnlyModal,
  ModalProps,
} from '@pass-frontend/common/components/modal/ui';
import { useIntl } from '@pass-frontend/common/i18n';
import { ServiceSectorName } from '@pass-frontend/data-access/common/edge';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';

export interface YouAreEligibleModalProps {
  onRequestedClose: ModalProps['onRequestedClose'];
  isOpen: ModalProps['isOpen'];
  registrationUrl: string;
  memberCode?: string;
  serviceSector?: string;
}

const StyledYouAreEligibleModal = styled(ClientOnlyModal)`
  &.modal {
    width: calc(100% - 32px);
    height: auto;
    margin: 16px;
    padding-bottom: 16px;

    ${MEDIA_QUERY.desktop} {
      width: 600px;
      padding: 40px;
    }
  }

  border-radius: 17px;
  .body {
    margin: 24px 0 0 0;
  }

  .close-btn {
    border: none;
    color: #222222;
    margin-bottom: 24px;

    .system-close {
      height: 14px;
      width: 14px;
    }
  }
`;
const ModalBody = styled.p`
  margin-bottom: 40px;

  .member-code {
    margin-bottom: 24px;
  }

  .modal-content-body-one {
    margin-bottom: 16px;
  }

  ${MEDIA_QUERY.mobile} {
    margin-bottom: 24px;

    .member-code {
      margin-bottom: 16px;
    }
  }
`;
const ModalTitle = styled.h1.attrs({
  className: 'is-display-flex is-h3 has-text-blue-1',
})`
  margin: 24px 0;

  ${MEDIA_QUERY.mobile} {
    margin: 16px 0;
  }
`;

const ContinueButton = styled.a.attrs({
  className: 'is-display-flex is-justify-content-end',
})`
  button {
    ${MEDIA_QUERY.mobile} {
      width: 100%;
      height: 32px;
      padding: 6px 0;
    }
  }

  &:hover {
    text-decoration: none;
  }
`;

export function YouAreEligibleModal(props: YouAreEligibleModalProps) {
  const { t } = useIntl();
  const {
    onRequestedClose,
    registrationUrl,
    serviceSector,
    memberCode,
    ...modalConfig
  } = props;
  const isServiceSectorGovernment =
    serviceSector === ServiceSectorName.Government;

  return (
    <StyledYouAreEligibleModal
      data-testid="you-are-eligible-modal"
      ariaHeadingId="youAreEligibleModal"
      ariaLabel={t('youAreEligibleModal.title')}
      onRequestedClose={onRequestedClose}
      overlayZIndex={1001}
      shouldHideHeading={true}
      className="modal"
      {...props}
    >
      <>
        {isServiceSectorGovernment && (
          <>
            <ModalTitle data-testid="gov-modal-title">
              {t('youAreEligibleModal.government.title')}
            </ModalTitle>
            <ModalBody className="is-body-2-regular">
              <div className="is-h4 has-text-gray-1 member-code">
                {memberCode}
              </div>
              <div className="modal-content-body-one">
                {t('youAreEligibleModal.government.bodyOne')}
              </div>
              <div>{t('youAreEligibleModal.government.bodyTwo')}</div>
            </ModalBody>
          </>
        )}
        {serviceSector === ServiceSectorName.Commercial && (
          <>
            <ModalTitle>{t('youAreEligibleModal.title')}</ModalTitle>
            <ModalBody className="is-body-2-regular">
              {t('youAreEligibleModal.body')}
            </ModalBody>
          </>
        )}
        <ContinueButton target="_blank" href={registrationUrl}>
          <button
            type="button"
            className="btn has-bg-blue-1 has-text-white is-body-1-bold "
            data-ui-element-name="continue-to-register-and-one-pass"
            onClick={(event) => {
              logAnalyticsEvent(
                EventType.ExternalNav,
                {
                  section: {
                    pageName: registrationUrl,
                  },
                },
                event.nativeEvent,
              );
            }}
          >
            {t('youAreEligibleModal.continueButton')}
          </button>
        </ContinueButton>
      </>
    </StyledYouAreEligibleModal>
  );
}

export default YouAreEligibleModal;
